import React, { useState, useEffect, useCallback } from 'react';
import {
  LayoutDashboard,
  Users,
  Building2,
  Shield,
  FolderKanban,
  Plug,
  Fingerprint,
  BarChart3,
  Settings,
  CreditCard,
  ScrollText,
  Workflow,
  AlertTriangle,
  FlaskConical,
  FlaskRound,
  Bot,
  ShieldCheck,
} from 'lucide-react';

// New shadcn/ui layout components
import {
  AppShell,
  AppShellMain,
  AppShellContent,
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarBrand,
  SidebarToggle,
  SidebarDivider,
  SidebarNavItem,
  useAppShell,
  useSidebar,
  Header,
  HeaderSearch,
  HeaderActions,
  HeaderAIButton,
  HeaderThemeToggle,
  HeaderNotifications,
  UserMenu,
} from './ui/layout';

import SearchModal from './SearchModal';
import { useNavigationCounts, formatBadgeCount } from '../hooks/useNavigationCounts';
import { useSettings } from '../hooks/useSettings';
import { usePlanFeatures, FEATURES } from '../hooks/usePlanFeatures';
import { useAdminAccess } from '../hooks/useAdmin';
import { BRAND } from '../config/brand';
import { useTestMode } from '../contexts/TestModeContext';

/**
 * Navigation items configuration
 * Matches the existing AppShell navigation
 */
/**
 * Navigation items configuration
 * featureRequired: name of feature from FEATURES that must be true to show this item
 */
const navItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'agent', label: 'Megan (AI Analyst)', icon: Bot },
  { id: 'alerts', label: 'Alerts', icon: AlertTriangle, badgeKey: 'open_alerts' },
  { id: 'applicants', label: 'Applicants', icon: Users, badgeKey: 'pending_applicants' },
  { id: 'companies', label: 'Companies', icon: Building2 },
  { id: 'screening', label: 'Screening', icon: Shield, badgeKey: 'unresolved_hits' },
  { id: 'cases', label: 'Cases', icon: FolderKanban, badgeKey: 'open_cases', featureRequired: FEATURES.CASES },
  { id: 'integrations', label: 'Integrations', icon: Plug },
  { id: 'dev-workbench', label: 'Dev Workbench', icon: FlaskConical },
  { id: 'workflow-builder', label: 'Workflow Builder', icon: Workflow, featureRequired: FEATURES.WORKFLOW_BUILDER },
  { divider: true },
  { id: 'device-intel', label: 'Device Intelligence', icon: Fingerprint },
  // { id: 'reusable-kyc', label: 'Reusable KYC', icon: Network, beta: true }, // Hidden until we have network scale - see .claude/plans/26-02-01-reusable-kyc-DRAFT.md
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { divider: true },
  { id: 'settings', label: 'Settings', icon: Settings },
  { id: 'billing', label: 'Billing & Usage', icon: CreditCard },
  { id: 'audit-log', label: 'Audit Log', icon: ScrollText, featureRequired: FEATURES.AUDIT_LOGS },
  { id: 'admin', label: 'Admin', icon: ShieldCheck, adminOnly: true },
];

/**
 * Inner shell component that uses the context providers
 */
function AppShellInner({ children, currentPage, onNavigate, user, onLogout }) {
  const { darkMode, toggleDarkMode } = useAppShell();
  const { setCollapsed } = useSidebar();
  const [searchOpen, setSearchOpen] = useState(false);
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const { isTestMode, toggleTestMode } = useTestMode();

  // Fetch plan features for feature gating
  const { data: planFeatures } = usePlanFeatures();
  // Super-admin access for the cross-tenant Admin section
  const { data: adminAccess } = useAdminAccess();

  // Filter nav items based on plan features + super-admin gating
  const filteredNavItems = navItems.filter((item) => {
    if (item.divider) return true;
    if (item.adminOnly) return adminAccess?.is_super_admin === true;
    if (!item.featureRequired) return true;
    // Only show if the feature is enabled in the plan
    return planFeatures?.[item.featureRequired] === true;
  });

  // Check if we're in workflow builder mode (maximize canvas space)
  const isWorkflowBuilder = currentPage === 'workflow-builder';

  // Auto-collapse sidebar on mobile or when in workflow builder
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768 || isWorkflowBuilder) {
        setCollapsed(true);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isWorkflowBuilder, setCollapsed]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setSearchOpen(false);
        setAiPanelOpen(false);
      }
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        setSearchOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Fetch real-time navigation badge counts
  const { data: navCounts } = useNavigationCounts();

  // Fetch tenant settings for company name
  const { data: settings } = useSettings();
  const rawTenantName = settings?.general?.company_name;
  const tenantName = typeof rawTenantName === 'object' && rawTenantName !== null
    ? (rawTenantName.value || 'My Workspace')
    : String(rawTenantName || 'My Workspace');

  // Helper to get badge count
  const getBadgeCount = useCallback((badgeKey) => {
    if (!badgeKey || !navCounts) return null;
    const rawCount = navCounts[badgeKey];
    const count = typeof rawCount === 'object' && rawCount !== null
      ? (rawCount.value ?? rawCount)
      : rawCount;
    return formatBadgeCount(count);
  }, [navCounts]);

  const bannerHeight = isTestMode ? 'top-[36px]' : 'top-0';

  return (
    <>
      {/* Test Mode Banner — full-width, fixed above everything (Stripe pattern) */}
      {isTestMode && (
        <div
          className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-5 h-[36px] bg-amber-500"
          role="status"
          aria-live="polite"
        >
          <div className="flex items-center gap-2 text-sm font-bold text-amber-950">
            <FlaskRound size={15} />
            <span>TEST MODE</span>
            <span className="font-normal text-amber-950/70 hidden sm:inline">— Viewing sandbox data</span>
          </div>
          <button
            onClick={toggleTestMode}
            className="px-3 py-1 text-xs font-semibold rounded-md bg-amber-950/15 hover:bg-amber-950/25 text-amber-950 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-950/50"
            aria-label="Exit test mode and return to live data"
          >
            Exit Test Mode
          </button>
        </div>
      )}

      {/* Sidebar — shifts down when test banner is visible */}
      <Sidebar className={isTestMode ? bannerHeight : undefined}>
        <SidebarHeader>
          <SidebarBrand
            logo="TG"
            name={BRAND.name}
            subtitle={tenantName}
          />
        </SidebarHeader>

        <SidebarContent>
          {filteredNavItems.map((item, idx) => {
            if (item.divider) {
              return <SidebarDivider key={`divider-${idx}`} />;
            }

            const badgeCount = getBadgeCount(item.badgeKey);

            return (
              <SidebarNavItem
                key={item.id}
                icon={item.icon}
                label={item.label}
                active={currentPage === item.id}
                badge={badgeCount}
                beta={item.beta}
                onClick={() => onNavigate?.(item.id)}
              />
            );
          })}
        </SidebarContent>

        {/* Test/Live mode toggle in sidebar footer */}
        <div className="mt-auto border-t border-grey-200 dark:border-grey-800 px-3 py-3">
          <button
            onClick={toggleTestMode}
            role="switch"
            aria-checked={isTestMode}
            aria-label={isTestMode ? 'Test mode is on. Click to switch to live mode.' : 'Live mode is on. Click to switch to test mode.'}
            className={`flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 ${
              isTestMode
                ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30'
                : 'text-grey-500 dark:text-grey-400 hover:bg-grey-100 dark:hover:bg-grey-800 border border-transparent'
            }`}
          >
            <FlaskRound size={16} />
            <span className="flex-1 text-left">{isTestMode ? 'Test Mode' : 'Live Mode'}</span>
            {/* Toggle pill */}
            <span className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 transition-colors ${
              isTestMode
                ? 'bg-amber-500 border-amber-500'
                : 'bg-grey-300 dark:bg-grey-600 border-grey-300 dark:border-grey-600'
            }`}>
              <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                isTestMode ? 'translate-x-4' : 'translate-x-0'
              }`} />
            </span>
          </button>
        </div>

        <SidebarToggle />
      </Sidebar>

      {/* Main content area — shifts down when test banner is visible */}
      <AppShellMain className={isTestMode ? 'pt-[36px]' : undefined}>
        {/* Header - hidden in workflow builder mode */}
        {!isWorkflowBuilder && (
          <Header>
            <HeaderSearch
              placeholder="Search applicants, companies, cases..."
              onClick={() => setSearchOpen(true)}
            />
            <HeaderActions>
              <HeaderAIButton onClick={() => setAiPanelOpen(true)} />
              <HeaderNotifications hasUnread />
              <HeaderThemeToggle darkMode={darkMode} onToggle={toggleDarkMode} />
              <UserMenu user={user} onNavigate={onNavigate} onLogout={onLogout} />
            </HeaderActions>
          </Header>
        )}

        {/* Page content */}
        <AppShellContent noPadding={isWorkflowBuilder}>
          {children}
        </AppShellContent>
      </AppShellMain>

      {/* Search Modal */}
      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* AI Panel Overlay */}
      {aiPanelOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40"
          onClick={() => setAiPanelOpen(false)}
        />
      )}

      {/* AI Panel */}
      <aside
        className={`fixed right-0 top-0 bottom-0 w-[400px] bg-white dark:bg-grey-900 border-l border-grey-200 dark:border-grey-800 z-50 transition-transform duration-300 ${
          aiPanelOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between p-5 border-b border-grey-200 dark:border-grey-800">
          <div className="flex items-center gap-2 font-semibold text-grey-900 dark:text-grey-50">
            <span className="text-blue-500">✨</span>
            AI Assistant
          </div>
          <button
            onClick={() => setAiPanelOpen(false)}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-grey-100 dark:hover:bg-grey-800 text-grey-500"
          >
            ×
          </button>
        </div>

        <div className="flex-1 p-5 overflow-y-auto">
          <div className="bg-grey-100 dark:bg-grey-800 rounded-xl p-4 mb-3">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-blue-500 mb-2">
              Quick Actions
            </div>
            <div className="text-sm text-grey-700 dark:text-grey-300 leading-relaxed">
              You have 3 applicants pending review with high-confidence matches.
              Would you like me to prepare a summary for batch review?
            </div>
          </div>

          <div className="bg-grey-100 dark:bg-grey-800 rounded-xl p-4">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-orange-500 mb-2">
              Risk Alert
            </div>
            <div className="text-sm text-grey-700 dark:text-grey-300 leading-relaxed">
              Detected potential sanctions match for "Sofia Reyes" against OFAC SDN list.
              Confidence: 85%. Review recommended.
            </div>
          </div>
        </div>

        <div className="p-5 border-t border-grey-200 dark:border-grey-800">
          <textarea
            className="w-full h-20 bg-grey-100 dark:bg-grey-800 rounded-lg p-3 text-sm text-grey-900 dark:text-grey-50 placeholder:text-grey-400 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            placeholder="Ask me anything about your compliance data..."
          />
        </div>
      </aside>
    </>
  );
}

/**
 * New AppShell using shadcn/ui components with Hyperkit styling
 *
 * Drop-in replacement for the existing AppShell.jsx
 * Uses modular layout components instead of inline CSS
 */
export default function AppShellNew({ children, currentPage, onNavigate, user, onLogout }) {
  // Check if we're in workflow builder mode to set initial collapsed state
  const isWorkflowBuilder = currentPage === 'workflow-builder';
  const defaultCollapsed = typeof window !== 'undefined' && (window.innerWidth < 768 || isWorkflowBuilder);

  return (
    <AppShell defaultCollapsed={defaultCollapsed} defaultDarkMode={true}>
      <AppShellInner
        currentPage={currentPage}
        onNavigate={onNavigate}
        user={user}
        onLogout={onLogout}
      >
        {children}
      </AppShellInner>
    </AppShell>
  );
}
